import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tenantApi, type TeamOverrideActiveSummary } from '../services/tenantApi';
import { ONCALL_OVERRIDE_CHANGED_EVENT } from '../shared/onCallOverrideEvents';
import { formatDateTime } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import { describeActiveOverride } from '../tenant-portal/components/onCallOverrideHelpers';
import './PortalBanner.css';

export const ONCALL_OVERRIDE_POLL_MS = 60_000;

interface Props {
  /** Opens the overrides console; only offered when the API reports canManage. */
  onManage?: () => void;
}

/** Tenant-wide "on-call override active" indicator (BL-043 UI-059): SignalR-driven with a 60s polling fallback. */
export const OnCallOverrideBanner: React.FC<Props> = ({ onManage }) => {
  useDateTimePreferences();
  const [summary, setSummary] = useState<TeamOverrideActiveSummary | null>(null);
  const inFlight = useRef(false);
  const rerun = useRef(false);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (inFlight.current) {
      rerun.current = true;
      return;
    }
    inFlight.current = true;
    try {
      const next = await tenantApi.getTeamOverrideActiveSummary();
      if (mounted.current) setSummary(next);
    } catch {
      // Informational only: keep the last known state rather than flashing an error in every view.
    } finally {
      inFlight.current = false;
      if (rerun.current && mounted.current) {
        rerun.current = false;
        void refresh();
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const onChange = () => void refresh();
    const interval = window.setInterval(onChange, ONCALL_OVERRIDE_POLL_MS);
    window.addEventListener(ONCALL_OVERRIDE_CHANGED_EVENT, onChange);
    window.addEventListener('nog:schedules:changed', onChange);
    window.addEventListener('focus', onChange);
    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      window.removeEventListener(ONCALL_OVERRIDE_CHANGED_EVENT, onChange);
      window.removeEventListener('nog:schedules:changed', onChange);
      window.removeEventListener('focus', onChange);
    };
  }, [refresh]);

  if (!summary || summary.count === 0 || summary.overrides.length === 0) return null;

  const formatWhen = (iso: string) => formatDateTime(iso, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const title = summary.overrides.length === 1 ? 'On-call override active' : `${summary.overrides.length} on-call overrides active`;

  return (
    <div className="portal-banner-stack" role="status" aria-live="polite" data-testid="oncall-override-banner">
      <div className="portal-banner warning">
        <div className="portal-banner-content">
          <div className="portal-banner-title">{title}</div>
          {summary.overrides.map(item => (
            <div className="portal-banner-message" key={item.overrideId} title={(item.scheduleNames || []).join(', ')}>
              {describeActiveOverride(item, formatWhen)}
            </div>
          ))}
        </div>
        {summary.canManage && onManage && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onManage}>Manage overrides</button>
        )}
      </div>
    </div>
  );
};
